import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Home } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BrandMark from "@/components/BrandMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}


export default function LoginPage() {
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, loginAsGuest } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Sesión iniciada. ¡Bienvenid@!");
      } else if (mode === "register") {
        await register(name, email, password);
        toast.success("Cuenta creada. ¡Bienvenid@!");
      } else {
        await fetch(`${API}/auth/forgot-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });
        toast.success("Si el correo existe, recibirás instrucciones para restablecer tu contraseña.");
        setMode("login");
        setLoading(false);
        return;
      }
      navigate("/campus");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleGuest = () => {
    loginAsGuest();
    toast.success("Has entrado como persona invitada");
    navigate("/campus");
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" data-testid="login-page">
      {/* Home button */}
      <Link
        to="/"
        data-testid="login-home-btn"
        aria-label="Volver al inicio"
        className="fixed top-5 right-5 z-50 flex items-center gap-2 bg-white/95 text-[#e65100] px-4 py-2.5 text-xs font-mono-tactical uppercase tracking-wider font-bold rounded-full shadow-lg hover:bg-white transition-colors"
      >
        <Home className="w-4 h-4" aria-hidden="true" />
        Inicio
      </Link>
      {/* Left panel */}
      <div className="relative md:w-1/2 bg-[#0f2027] text-[#fffbf2] flex items-end md:items-center overflow-hidden min-h-[280px] md:min-h-screen">
        <img
          src="https://images.unsplash.com/photo-1635150632618-b09762cf0690?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxjb2FzdCUyMGd1YXJkJTIwcmVzY3VlJTIwdGVhbXxlbnwwfHx8fDE3OTM4NjUzNzh8MA&ixlib=rb-4.1.0&q=85"
          alt="Rescate marítimo"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative z-10 p-8 md:p-16 space-y-6">
          <Link to="/" data-testid="login-back-home-link" className="inline-flex items-center gap-3">
            <BrandMark className="w-24 h-16" />
            <div className="leading-none">
              <span className="block font-display text-xl tracking-wider uppercase">PROTECCIÓN CIVIL VIGO</span>
              <span className="block text-[11px] tracking-widest text-[#e65100] font-mono-tactical uppercase">
                Búsqueda y Salvamento
              </span>
            </div>
          </Link>
          <h1 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] tracking-tight">
            Entrar a Búsqueda y Salvamento de Galicia
          </h1>
          <p className="text-sm md:text-base text-[#fffbf2]/75 max-w-md">
            Accede al campus de teleformación: primeros auxilios, salvamento marítimo y búsqueda de personas, con
            instructores operativos en activo.
          </p>
        </div>
      </div>

      {/* Right panel */}
      <div className="md:w-1/2 bg-[#e65100] flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md space-y-6">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode("login")}
              data-testid="login-mode-login-btn"
              className={`flex-1 py-2 text-xs font-mono-tactical uppercase tracking-widest font-bold rounded transition-colors ${
                mode === "login" ? "bg-[#0f2027] text-white" : "bg-white/20 text-white/80 hover:bg-white/30"
              }`}
            >
              Acceder
            </button>
            <button
              type="button"
              onClick={() => setMode("register")}
              data-testid="login-mode-register-btn"
              className={`flex-1 py-2 text-xs font-mono-tactical uppercase tracking-widest font-bold rounded transition-colors ${
                mode === "register" ? "bg-[#0f2027] text-white" : "bg-white/20 text-white/80 hover:bg-white/30"
              }`}
            >
              Crear cuenta
            </button>
          </div>

          <form onSubmit={handleSubmit} data-testid="login-form" className="space-y-4">
            {mode === "register" && (
              <input
                type="text"
                required
                minLength={2}
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="login-name-input"
                placeholder="Nombre completo"
                className="w-full px-4 py-3.5 text-sm rounded bg-white text-[#0f2027] border-2 border-transparent focus:border-[#0f2027] outline-none"
              />
            )}
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              placeholder="Nombre de usuario"
              className="w-full px-4 py-3.5 text-sm rounded bg-white text-[#0f2027] border-2 border-transparent focus:border-[#0f2027] outline-none"
            />
            {mode !== "forgot" && (
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                placeholder="Contraseña"
                className="w-full px-4 py-3.5 text-sm rounded bg-white text-[#0f2027] border-2 border-transparent focus:border-[#0f2027] outline-none"
              />
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full py-3.5 bg-[#0f2027] text-white text-xs font-mono-tactical uppercase tracking-widest font-bold rounded hover:bg-[#162c35] transition-colors disabled:opacity-60"
            >
              {loading ? "Un momento..." : mode === "login" ? "Acceder" : mode === "register" ? "Crear cuenta" : "Enviar instrucciones"}
            </button>
          </form>

          <button
            type="button"
            onClick={() => setMode(mode === "forgot" ? "login" : "forgot")}
            data-testid="login-forgot-link"
            className="block mx-auto text-xs text-white/80 hover:text-white underline underline-offset-2"
          >
            {mode === "forgot" ? "Volver a acceder" : "¿Olvidó su contraseña?"}
          </button>

          <div className="pt-6 border-t border-white/25 space-y-3 text-center">
            <p className="text-sm text-white font-semibold">Algunos cursos permiten el acceso de invitados</p>
            <button
              type="button"
              onClick={handleGuest}
              data-testid="login-guest-btn"
              className="px-6 py-3 bg-white text-[#e65100] text-xs font-mono-tactical uppercase tracking-widest font-bold rounded hover:bg-[#fffbf2] transition-colors"
            >
              Entrar como persona invitada
            </button>
            <p className="text-[11px] text-white/70 font-mono-tactical">Español - Internacional (es)</p>
          </div>
        </div>
      </div>
    </div>
  );
}
